import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { FiCheckCircle, FiMail, FiShield } from 'react-icons/fi';
import { Footer, MarketingHeader, SEO } from '@/components';
import { track } from '@/lib/analytics';

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
} as const;

const SUPPORT_EMAIL = 'team@harborcoparent.com';

const MAILTO = [
  `mailto:${SUPPORT_EMAIL}`,
  '?subject=',
  encodeURIComponent('Harbor fee waiver request'),
  '&body=',
  encodeURIComponent(
    [
      'Harbor account email:',
      '',
      'Which category (court fee waiver / public benefits / domestic violence survivor):',
      '',
      'I have attached a photo or PDF of my proof.',
      '',
    ].join('\n'),
  ),
].join('');

const QUALIFYING = [
  {
    title: 'Court fee waiver',
    body: 'An approved court fee waiver for your case. In California that is often FW-001 (or the order granting it). Other states have an equivalent form.',
  },
  {
    title: 'Means-tested public benefits',
    body: 'Current enrollment in SNAP, SSI, TANF, Medicaid, or WIC. A recent award letter or benefits statement is enough.',
  },
  {
    title: 'Domestic violence survivor',
    body: 'You, your attorney, or a legal-services organization can email us. A protective order, shelter letter, or advocate letter is enough. We do not need a detailed account of what happened.',
  },
] as const;

const STEPS = [
  'Create a Harbor account (we grant access to an existing email).',
  'Email team@harborcoparent.com from that same address.',
  'Say which category you qualify under and attach a photo or PDF of the proof.',
  'We review by hand and, if approved, grant 12 months of full access.',
] as const;

const FAQS = [
  {
    question: 'What does a Harbor fee waiver cover?',
    answer:
      'An approved waiver covers 12 months of full Harbor access for that parent: messaging, calendar, expenses, and exports. It does not cover court filing fees or the other parent’s subscription.',
  },
  {
    question: 'What proof do you need?',
    answer:
      'The same kind of proof other co-parenting apps use: an approved court fee waiver, a current means-tested benefits letter (SNAP, SSI, TANF, Medicaid, or WIC), or documentation that you are a domestic violence survivor. Photos or PDFs are fine. Do not mail originals.',
  },
  {
    question: 'How long does review take?',
    answer:
      'We review requests by hand, usually within a few business days. If we need a clearer copy of the document, we will reply to the same email.',
  },
  {
    question: 'Does this expire?',
    answer:
      'Yes. Access is granted for 12 months. Email us again before it ends with current proof if you still qualify.',
  },
  {
    question: 'What happens to the documents I send?',
    answer:
      'We use them only to review the request. After we decide, we delete the attachments. Do not send documents you are not allowed to share.',
  },
  {
    question: 'Is Harbor affiliated with the court?',
    answer:
      'No. Harbor is a private company. A fee waiver here is not a court program and is not an endorsement by any court, Family Court Services office, or self-help center.',
  },
] as const;

function handleEmailClick() {
  track('Click Fee Waiver Email');
}

function FeeWaiversPage() {
  useEffect(() => {
    track('Fee Waivers Screen');
  }, []);

  return (
    <div className='min-h-screen bg-white'>
      <SEO
        title='Fee Waivers for Co-Parents | Harbor'
        description='Harbor waives the $49.99/year subscription for parents who qualify. Email us with the same kind of proof other co-parenting apps ask for: a court fee waiver, public benefits, or survivor documentation.'
        path='/fee-waivers'
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        }}
      />
      <MarketingHeader />

      <section className='bg-primary/5 pt-32 pb-12'>
        <div className='max-w-3xl mx-auto px-6 text-center'>
          <p
            className='text-primary font-semibold text-sm uppercase tracking-wide mb-3'
            style={{ ...nunito, fontWeight: 600 }}
          >
            Fee waivers
          </p>
          <h1
            className='text-4xl md:text-5xl font-bold text-gray-900 mb-4'
            style={{ ...nunito, fontWeight: 700 }}
          >
            If you cannot afford Harbor, email us
          </h1>
          <p
            className='text-xl text-gray-600 leading-relaxed'
            style={{ ...nunito, fontWeight: 400 }}
          >
            Harbor is $9.99 a month or $49.99 a year per person. Parents who
            qualify for a court fee waiver, means-tested public benefits, or who
            are domestic violence survivors can get 12 months of full access at
            no cost.
          </p>
          <a
            href={MAILTO}
            onClick={handleEmailClick}
            className='inline-flex items-center gap-2 mt-8 bg-primary hover:bg-primary-hover text-white font-semibold px-8 py-4 rounded-lg transition-all text-lg'
            style={{ ...nunito, fontWeight: 600 }}
          >
            <FiMail className='w-5 h-5' />
            Email a waiver request
          </a>
        </div>
      </section>

      <main className='max-w-3xl mx-auto px-6 py-14'>
        <section className='mb-14'>
          <h2
            className='text-2xl font-bold text-gray-900 mb-6'
            style={{ ...nunito, fontWeight: 700 }}
          >
            Who qualifies
          </h2>
          <div className='space-y-4'>
            {QUALIFYING.map((item) => (
              <div
                key={item.title}
                className='rounded-2xl border border-gray-100 bg-gray-50 p-6'
              >
                <h3
                  className='text-lg font-semibold text-gray-900 mb-2'
                  style={{ ...nunito, fontWeight: 600 }}
                >
                  {item.title}
                </h3>
                <p className='text-gray-600 leading-relaxed' style={nunito}>
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className='mb-14'>
          <h2
            className='text-2xl font-bold text-gray-900 mb-6'
            style={{ ...nunito, fontWeight: 700 }}
          >
            How to apply
          </h2>
          <ol className='space-y-4'>
            {STEPS.map((step, index) => (
              <li key={step} className='flex gap-4'>
                <span
                  className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold'
                  style={{ ...nunito, fontWeight: 700 }}
                >
                  {index + 1}
                </span>
                <p
                  className='text-gray-700 leading-relaxed pt-1'
                  style={nunito}
                >
                  {step}
                </p>
              </li>
            ))}
          </ol>
          <p className='mt-6 text-gray-600 leading-relaxed' style={nunito}>
            Write from the Harbor account email, or include that email in the
            message so we can find you. A photo of the letter is enough.
          </p>
        </section>

        <section className='mb-14 grid gap-4 sm:grid-cols-2'>
          <div className='rounded-2xl border border-gray-100 p-6'>
            <FiCheckCircle className='w-6 h-6 text-primary mb-3' />
            <h3
              className='font-semibold text-gray-900 mb-2'
              style={{ ...nunito, fontWeight: 600 }}
            >
              What you get
            </h3>
            <p className='text-gray-600 leading-relaxed' style={nunito}>
              Twelve months of the same Harbor plan paying parents get. Renewable
              with current proof.
            </p>
          </div>
          <div className='rounded-2xl border border-gray-100 p-6'>
            <FiShield className='w-6 h-6 text-primary mb-3' />
            <h3
              className='font-semibold text-gray-900 mb-2'
              style={{ ...nunito, fontWeight: 600 }}
            >
              What we do with proof
            </h3>
            <p className='text-gray-600 leading-relaxed' style={nunito}>
              Review the request, grant access if you qualify, then delete the
              attachments. We do not share them with the other parent or a court.
            </p>
          </div>
        </section>

        <section className='mb-14'>
          <h2
            className='text-2xl font-bold text-gray-900 mb-6'
            style={{ ...nunito, fontWeight: 700 }}
          >
            Questions
          </h2>
          <div className='space-y-6'>
            {FAQS.map((faq) => (
              <div key={faq.question} className='border-b border-gray-200 pb-6'>
                <h3
                  className='text-lg font-semibold text-gray-900 mb-2'
                  style={{ ...nunito, fontWeight: 600 }}
                >
                  {faq.question}
                </h3>
                <p className='text-gray-600 leading-relaxed' style={nunito}>
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className='rounded-2xl bg-primary px-6 py-10 text-center'>
          <h2
            className='text-2xl font-bold text-white mb-3'
            style={{ ...nunito, fontWeight: 700 }}
          >
            Ready to request a waiver?
          </h2>
          <p className='text-white/85 mb-6' style={nunito}>
            Email {SUPPORT_EMAIL} with your Harbor account email and proof.
          </p>
          <a
            href={MAILTO}
            onClick={handleEmailClick}
            className='inline-block bg-white hover:bg-gray-100 text-primary font-semibold px-8 py-4 rounded-lg transition-all text-lg'
            style={{ ...nunito, fontWeight: 600 }}
          >
            Email {SUPPORT_EMAIL}
          </a>
          <p className='mt-6 text-sm text-white/70' style={nunito}>
            Paying the regular $49.99/year plan?{' '}
            <Link
              to='/'
              search={{ hero: undefined }}
              className='underline hover:text-white'
            >
              Go to Harbor
            </Link>
            .
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export const Route = createFileRoute('/fee-waivers')({
  component: FeeWaiversPage,
});
